/**
 * Stage 7 — Grade Estimation (strict gate).
 *
 * Embeds BlockModelTab. Creating an IDW or Ordinary Kriging model fires
 * the Stage 7 (and Stage 8) pipeline-state completion hooks server-side.
 *
 * Honesty banner: Master Build Spec §Part 3 asks for kd-tree neighbourhood
 * search + LOOCV cross-validation. As of Session E-1 the estimation core
 * (`backend/estimation.py`) uses a full-matrix N+1 Ordinary Kriging solve
 * and has no cross-validation; this is a known P1 gap flagged upstream to
 * the user rather than faked in the UI.
 */
import { useEffect, useState } from "react";
import BlockModelTab from "@/components/tabs/BlockModelTab";
import AIAutopilotPanel from "../AIAutopilotPanel";
import { AlertTriangle } from "lucide-react";
import StageModeSwitcher, { useStageMode } from "@/components/StageModeSwitcher";

export default function Stage7GradeEstimation({ projectId, onCompleted }) {
  const [_smMode, _setSmMode] = useStageMode(projectId, 7, "ai");
  const [pollActive, setPollActive] = useState(false);
  useEffect(() => {
    if (!pollActive || !projectId) return undefined;
    const t = setInterval(() => onCompleted?.(), 3500);
    const stop = setTimeout(() => setPollActive(false), 60_000);
    return () => { clearInterval(t); clearTimeout(stop); };
  }, [pollActive, projectId, onCompleted]);

  return (
    <div data-testid="stage7-content" className="space-y-4">
      <div className="flex items-center justify-between gap-3 pb-2 border-b border-neutral-100">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-medium">Input mode</div>
        <StageModeSwitcher mode={_smMode} onChange={_setSmMode} testidPrefix="stage7-mode" />
      </div>
      <AIAutopilotPanel
        projectId={projectId}
        title="AI Auto-Fill · IDW block model"
        description="Derive block size, grid, cutoff, and base grade from your uploaded drillholes, then run IDW grade estimation in one click."
        autoFillPath="auto-fill/idw"
        executePath={`/projects/${projectId}/block-models`}
        buildPayload={(f) => ({
          name: "AI IDW Block Model",
          origin: [f.origin_x ?? 0, f.origin_y ?? 0, f.origin_z ?? 0],
          block_size: f.block_size ?? 10.0,
          dimensions: [f.nx ?? 20, f.ny ?? 20, f.nz ?? 10],
          cutoff_grade: f.cutoff_grade ?? 0.3,
          base_grade: f.base_grade ?? 0.5,
        })}
        successLabel="Block model generated — Stages 7 & 8 complete"
        buttonLabel="Run IDW with AI values"
        onSuccess={() => { setPollActive(true); onCompleted?.(); }}
        testid="stage7-autopilot"
      />

      <div className="border border-neutral-200 bg-neutral-50 rounded p-3 text-[12px] text-neutral-800">
        <span className="text-[#4CAF50] font-medium">Manual mode:</span>{" "}
        configure IDW or Ordinary Kriging below and click <em>Generate</em>. A
        successful model auto-completes Stage 7 & Stage 8.
        <button
          onClick={() => { setPollActive(true); onCompleted?.(); }}
          className="ml-3 text-[#4CAF50] hover:text-[#2E7D32] underline text-[11.5px]"
          data-testid="stage7-refresh-state">
          Refresh state
        </button>
      </div>

      <div className="border border-neutral-200 rounded-md bg-neutral-50 overflow-hidden">
        <BlockModelTab projectId={projectId} />
      </div>
    </div>
  );
}
